/*
 * S&F Conversion Analytics – Storefront interaction layer
 * Version: 1.0.0
 *
 * Purpose:
 * - publishes shop-specific custom events to Shopify Customer Events
 * - mirrors relevant events to Microsoft Clarity when Clarity is active
 * - does NOT collect form field values, names, email addresses, phone numbers,
 *   postal addresses, payment data, free-text search terms or other PII
 */
(function () {
  'use strict';

  if (window.__SF_CONVERSION_ANALYTICS_INITIALIZED__) return;
  window.__SF_CONVERSION_ANALYTICS_INITIALIZED__ = true;

  var VERSION = '1.0.0';
  var PREFIX = 'sf_analytics:';
  var context = window.SF_CONVERSION_CONTEXT || {};
  var fired = new Set();
  var pendingShopifyEvents = [];
  var shopifyPollTimer = null;
  var clarityTagsApplied = false;
  var startedAt = Date.now();

  function asString(value, maxLength) {
    if (value === null || value === undefined) return null;
    var str = String(value);
    return str.length > (maxLength || 120) ? str.slice(0, maxLength || 120) : str;
  }

  function asNumber(value) {
    var n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  function deviceType() {
    var width = Math.min(window.innerWidth || 9999, (window.screen && window.screen.width) || 9999);
    if (width <= 767) return 'mobile';
    if (width <= 1024) return 'tablet';
    return 'desktop';
  }

  function getQueryParam(name) {
    try {
      return new URL(window.location.href).searchParams.get(name);
    } catch (e) {
      return null;
    }
  }

  function referrerHost() {
    if (!document.referrer) return null;
    try {
      return new URL(document.referrer).hostname.toLowerCase();
    } catch (e) {
      return null;
    }
  }

  function trafficSource() {
    var utmSource = (getQueryParam('utm_source') || '').toLowerCase();
    var ref = referrerHost() || '';

    if (utmSource) {
      if (utmSource.indexOf('idealo') !== -1) return 'idealo';
      if (utmSource.indexOf('google') !== -1) return 'google';
      if (utmSource.indexOf('bing') !== -1 || utmSource.indexOf('microsoft') !== -1) return 'bing';
      if (utmSource.indexOf('meta') !== -1 || utmSource.indexOf('facebook') !== -1 || utmSource.indexOf('instagram') !== -1) return 'meta';
      return asString(utmSource, 80);
    }

    if (!ref) return 'direct';
    if (ref.indexOf('idealo.') !== -1) return 'idealo';
    if (ref.indexOf('google.') !== -1) return 'google_organic';
    if (ref.indexOf('bing.') !== -1) return 'bing_organic';
    if (ref.indexOf('facebook.') !== -1 || ref.indexOf('instagram.') !== -1) return 'meta_referral';
    if (ref.indexOf(window.location.hostname.toLowerCase()) !== -1) return 'internal';
    return 'referral';
  }

  function basePayload(extra) {
    var product = context.product || {};
    var payload = {
      schema_version: 1,
      tracker_version: VERSION,
      page_type: asString(context.page_type || document.body.getAttribute('data-template') || 'unknown', 50),
      template: asString(context.template, 80),
      device: deviceType(),
      traffic_source: trafficSource(),
      utm_source: asString(getQueryParam('utm_source'), 100),
      utm_medium: asString(getQueryParam('utm_medium'), 100),
      utm_campaign: asString(getQueryParam('utm_campaign'), 140),
      product_id: asString(product.id, 80),
      product_handle: asString(product.handle, 160),
      variant_id: asString(product.variant_id, 80),
      product_price_cents: asNumber(product.price_cents),
      currency: asString(context.currency, 8),
      elapsed_ms: Math.max(0, Date.now() - startedAt)
    };

    if (extra && typeof extra === 'object') {
      Object.keys(extra).forEach(function (key) {
        var value = extra[key];
        if (value === undefined || typeof value === 'function' || typeof value === 'object') return;
        if (typeof value === 'string') payload[key] = asString(value, 180);
        else if (typeof value === 'number') payload[key] = Number.isFinite(value) ? value : null;
        else if (typeof value === 'boolean') payload[key] = value;
        else if (value === null) payload[key] = null;
      });
    }

    return payload;
  }

  function shopifyReady() {
    return !!(window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function');
  }

  function flushShopifyQueue() {
    if (!shopifyReady()) return false;

    while (pendingShopifyEvents.length) {
      var entry = pendingShopifyEvents.shift();
      try {
        var result = window.Shopify.analytics.publish(PREFIX + entry.name, entry.payload);
        if (result && typeof result.catch === 'function') result.catch(function () {});
      } catch (e) {
        // Analytics must never impact the storefront UX.
      }
    }
    return true;
  }

  function queueShopify(name, payload) {
    pendingShopifyEvents.push({ name: name, payload: payload });
    if (flushShopifyQueue()) return;

    if (!shopifyPollTimer) {
      var attempts = 0;
      shopifyPollTimer = window.setInterval(function () {
        attempts += 1;
        if (flushShopifyQueue() || attempts >= 40) {
          window.clearInterval(shopifyPollTimer);
          shopifyPollTimer = null;
          if (attempts >= 40) pendingShopifyEvents.length = 0;
        }
      }, 250);
    }
  }

  function clarityAvailable() {
    return typeof window.clarity === 'function';
  }

  function applyClarityTags() {
    if (clarityTagsApplied || !clarityAvailable()) return;

    var product = context.product || {};
    var tags = {
      sf_page_type: context.page_type || 'unknown',
      sf_device: deviceType(),
      sf_traffic_source: trafficSource(),
      sf_product_id: product.id || null,
      sf_product_handle: product.handle || null,
      sf_utm_source: getQueryParam('utm_source'),
      sf_utm_medium: getQueryParam('utm_medium'),
      sf_utm_campaign: getQueryParam('utm_campaign')
    };

    Object.keys(tags).forEach(function (key) {
      if (tags[key] === null || tags[key] === undefined || tags[key] === '') return;
      try {
        window.clarity('set', key, asString(tags[key], 250));
      } catch (e) {}
    });

    clarityTagsApplied = true;
  }

  function sendToClarity(name, options) {
    if (!clarityAvailable()) return;
    applyClarityTags();

    try {
      window.clarity('event', 'sf_' + name.replace(/[^a-zA-Z0-9_\-]/g, '_'));
      if (options && options.upgrade) {
        window.clarity('upgrade', 'sf_' + name.replace(/[^a-zA-Z0-9_\-]/g, '_'));
      }
      if (options && options.tag) {
        window.clarity('set', options.tag, 'yes');
      }
    } catch (e) {}
  }

  function publish(name, extra, options) {
    if (!name || !/^[a-z0-9_\-]+$/i.test(name)) return;
    var payload = basePayload(extra);
    queueShopify(name, payload);
    sendToClarity(name, options || {});
  }

  function publishOnce(key, name, extra, options) {
    if (fired.has(key)) return;
    fired.add(key);
    publish(name, extra, options);
  }

  function closestProductForm(element) {
    if (!element || !element.closest) return null;
    return element.closest('form[action*="/cart/add"]');
  }

  function classifyAccordion(summary) {
    var explicit = summary.closest('[data-sf-track]');
    if (explicit) return explicit.getAttribute('data-sf-track');

    var label = ((summary.textContent || '') + ' ' + (summary.getAttribute('aria-label') || '')).toLowerCase();
    if (label.indexOf('versand') !== -1 || label.indexOf('liefer') !== -1) return 'shipping_opened';
    if (label.indexOf('retoure') !== -1 || label.indexOf('rückgabe') !== -1 || label.indexOf('widerruf') !== -1) return 'returns_opened';
    if (label.indexOf('sicherheit') !== -1 || label.indexOf('hinweis') !== -1 || label.indexOf('warnung') !== -1) return 'safety_opened';
    if (label.indexOf('produktdetail') !== -1 || label.indexOf('details') !== -1 || label.indexOf('spezifikation') !== -1) return 'details_opened';
    return null;
  }

  function setupTimeEngagement() {
    [10, 30, 60].forEach(function (seconds) {
      window.setTimeout(function () {
        if (document.visibilityState !== 'visible') return;
        publishOnce('engaged_' + seconds, 'pdp_engaged_' + seconds + 's', { seconds: seconds });
      }, seconds * 1000);
    });
  }

  function setupScrollDepth() {
    var thresholds = [25, 50, 75, 90];
    var scheduled = false;

    function measure() {
      scheduled = false;
      var doc = document.documentElement;
      var scrollable = Math.max(1, doc.scrollHeight - window.innerHeight);
      var pct = Math.round((window.scrollY / scrollable) * 100);
      thresholds.forEach(function (threshold) {
        if (pct >= threshold) {
          publishOnce('scroll_' + threshold, 'scroll_' + threshold, { depth_percent: threshold });
        }
      });
    }

    window.addEventListener('scroll', function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(measure);
    }, { passive: true });
    measure();
  }

  function setupVisibilityTracking() {
    if (!('IntersectionObserver' in window)) return;

    var normalSelectors = [
      'form[action*="/cart/add"] button[name="add"]',
      'form[action*="/cart/add"] [type="submit"]',
      '[data-add-to-cart]',
      '[data-product-form-submit]'
    ];
    var stickySelectors = [
      '[data-sf-sticky-atc]',
      '.sf-sticky-atc',
      '.sticky-atc',
      '.product-sticky-bar',
      '.sticky-add-to-cart'
    ];

    var seen = new Set();
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.4) return;
        var el = entry.target;
        if (seen.has(el)) return;
        seen.add(el);

        var isSticky = stickySelectors.some(function (selector) {
          return el.matches(selector) || !!el.closest(selector);
        });

        if (isSticky) {
          publishOnce('event:sticky_atc_visible', 'sticky_atc_visible', {}, { tag: 'sf_sticky_atc_seen' });
        } else {
          publishOnce('event:atc_visible', 'atc_visible', {}, { tag: 'sf_atc_seen' });
        }
        observer.unobserve(el);
      });
    }, { threshold: [0.4, 0.75] });

    function observeMatches() {
      normalSelectors.concat(stickySelectors).forEach(function (selector) {
        document.querySelectorAll(selector).forEach(function (el) {
          if (!seen.has(el)) observer.observe(el);
        });
      });
    }

    observeMatches();
    var mo = new MutationObserver(observeMatches);
    mo.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(function () { mo.disconnect(); }, 15000);
  }

  function setupInteractionTracking() {
    document.addEventListener('click', function (event) {
      var target = event.target && event.target.closest ? event.target.closest('*') : null;
      if (!target) return;

      var explicit = target.closest('[data-sf-track]');
      if (explicit) {
        var explicitEvent = explicit.getAttribute('data-sf-track');
        if (explicitEvent && /^[a-z0-9_\-]+$/i.test(explicitEvent)) {
          publishOnce('event:' + explicitEvent, explicitEvent, { trigger: 'explicit_data_attribute' });
        }
      }

      var productForm = closestProductForm(target);
      if (productForm) {
        var addButton = target.closest('button[name="add"], [type="submit"], [data-add-to-cart], [data-product-form-submit]');
        if (addButton) {
          publishOnce('event:atc_click', 'atc_click', { trigger: 'product_form' }, { upgrade: true, tag: 'sf_atc_clicked' });
          return;
        }
      }

      var checkout = target.closest('[name="checkout"], [data-checkout], a[href*="/checkout"], button[name="checkout"]');
      if (checkout) {
        publishOnce('event:checkout_click', 'checkout_click', { trigger: 'checkout_control' }, { upgrade: true, tag: 'sf_checkout_clicked' });
        return;
      }

      var cartLink = target.closest('a[href="/cart"], a[href$="/cart"], [data-cart-icon-bubble], [data-cart-toggle], [data-cart-drawer-open]');
      if (cartLink) {
        publishOnce('event:cart_open_click', 'cart_open_click', { trigger: 'cart_control' }, { tag: 'sf_cart_opened' });
      }

      var idealo = target.closest('a[href*="idealo"], [data-idealo], .idealo-badge, [class*="idealo"]');
      if (idealo) {
        publishOnce('event:idealo_badge_click', 'idealo_badge_click', { trigger: 'idealo' }, { tag: 'sf_idealo_interaction' });
      }

      var gallery = target.closest('[data-media-id], [data-thumbnail], .thumbnail, .product__media-item, .product-gallery__thumbnail, .product__media-toggle');
      if (gallery) {
        publishOnce('event:gallery_interaction', 'gallery_interaction', { trigger: 'gallery' }, { tag: 'sf_gallery_interacted' });
      }

      var quantity = target.closest('.quantity__button, [data-quantity-plus], [data-quantity-minus], [name="plus"], [name="minus"]');
      if (quantity) {
        publishOnce('event:quantity_changed', 'quantity_changed', { trigger: 'quantity_control' }, { tag: 'sf_quantity_changed' });
      }

      var remove = target.closest('[data-cart-remove], cart-remove-button, a[href*="/cart/change"], [class*="cart-remove"]');
      if (remove) {
        publishOnce('event:cart_remove_click', 'cart_remove_click', { trigger: 'cart_remove_control' });
      }

      var summary = target.closest('summary');
      if (summary) {
        window.setTimeout(function () {
          var details = summary.parentElement;
          if (!details || details.tagName !== 'DETAILS' || !details.open) return;
          var accordionEvent = classifyAccordion(summary);
          if (accordionEvent) {
            publishOnce('event:' + accordionEvent, accordionEvent, { trigger: 'accordion' }, { tag: 'sf_' + accordionEvent });
          }
        }, 0);
      }
    }, true);

    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || !target.matches) return;

      if (target.matches('form[action*="/cart/add"] [name="id"]')) {
        var newVariant = asString(target.value, 80);
        publishOnce('variant:' + newVariant, 'variant_changed', { variant_id_selected: newVariant });
      }

      if (target.matches('input[name="quantity"], select[name="quantity"], .quantity__input')) {
        publishOnce('event:quantity_changed', 'quantity_changed', { trigger: 'quantity_input' }, { tag: 'sf_quantity_changed' });
      }
    }, true);

    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.matches) return;
      if (form.matches('form[action*="/cart/add"]')) {
        publishOnce('event:atc_submit', 'atc_submit', { trigger: 'form_submit' }, { upgrade: true, tag: 'sf_atc_submitted' });
      }
    }, true);

    // Shopify's standardized storefront event layer (introduced in 2026).
    document.addEventListener('shopify:cart:lines-update', function () {
      publish('cart_ui_updated', { trigger: 'shopify_standard_storefront_event' });
    });
  }

  function initialize() {
    applyClarityTags();

    if ((context.page_type || '').toLowerCase() === 'product' || context.product) {
      setupTimeEngagement();
      setupScrollDepth();
      setupVisibilityTracking();
    }

    setupInteractionTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
